import React, { useContext, useRef } from 'react';

import UserCreateDialog, { UserCreateDialogHandle } from 'components/UserCreateDialog/UserCreateDialog';
import { useUserDefaultLookupSearchFields } from 'components/GMetrixTable/gmetrixTableSearchFields';
import UserEditDialog, { UserEditDialogHandle } from 'components/UserEditDialog/UserEditDialog';
import { UserGMetrixTable } from 'components/UserGMetrixTable/UserGMetrixTable';
import { useRegisterHeaderControls } from 'components/layout';
import { NewUser, User, UsersApi } from 'apis/UsersApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePartners } from 'pages/partners/context';
import { useNotification } from 'app/notifications';
import { PartnerDetails } from 'apis/PartnerApi';
import { useSuperState } from 'hooks';

import 'pages/partners/components/UsersTab/styles/users-tab.scss';

export type UserStageOption = 'Table' | 'User';

type UsersTabProps = {
    partnerDetails: PartnerDetails;
};

export const UsersTab = (props: UsersTabProps) => {

    const { partnerDetails } = props;

    const [state, setState] = useSuperState({
        loadingGridData: true,
        searchTerm: '',
        users: [] as User[]
    });
    const { loadingGridData, searchTerm, users } = state;

    const { formatMessage } = useIntl();
    const { permissions } = usePartners();
    const { catchWithToast, onSuccessToast } = useNotification();
    const userDefaultLookupSearchFields = useUserDefaultLookupSearchFields();

    const userCreateDialogRef = useRef<UserCreateDialogHandle>(null);
    const userEditDialogRef = useRef<UserEditDialogHandle>(null);

    // Dial the "Add User Account" CTA out to the partner detail page header.
    useRegisterHeaderControls({
        cta: {
            disabled: loadingGridData || !permissions.UserEdit,
            label: (
                <div className="d-inline-flex align-items-center justify-content-center flex-fill gap-1">
                    <i className="fa-solid fa-plus" />
                    <span>
                        <FormattedMessage defaultMessage="Add User" id="add_user.daaf2592" />
                    </span>
                </div>
            ),
            onClick: () => userCreateDialogRef.current?.show()
        }
    });

    const onDetailsItemClick = async (dataItem: User) => {

        userEditDialogRef.current?.show(dataItem);

    };

    const getAllUsers = async () => {

        try {

            const users = await UsersApi.GetAll(1, partnerDetails.ID);
            setState({ users });

        } catch (e) {

            console.error(e);

        } finally {

            setState({ loadingGridData: false });

        }

    };

    const customCreateClick = async (newPartnerUserObj: NewUser, permissionsCSV: string, existingUser: boolean, allowUserToSetPassword: boolean) => {

        userCreateDialogRef.current?.hide();

        try {

            if (existingUser) {

                await UsersApi.Update(newPartnerUserObj, permissionsCSV, partnerDetails.ID, partnerDetails.PartnerType);

            } else {

                await UsersApi.Create(newPartnerUserObj, permissionsCSV, partnerDetails.ID, partnerDetails.PartnerType, allowUserToSetPassword);

            }

            onSuccessToast(formatMessage({ id: 'succesfully_created_user.ca91eb24', defaultMessage: 'Succesfully Created User' }));

        } catch (e) {

            catchWithToast(e, existingUser
                ? formatMessage({ id: 'failed_to_update_user.34e2ba9a', defaultMessage: 'Failed to update user.' })
                : formatMessage({ id: 'failed_to_create_user.99982644', defaultMessage: 'Failed to create user.' }));

        } finally {

            getAllUsers();

        }

    };

    return (
        <>
            <UserGMetrixTable
                columnData={[
                    { field: 'UserProfileID', hidden: true, title: formatMessage({ id: 'id.e9650a61', defaultMessage: 'ID' }) },
                    { field: 'FirstName', title: formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' }) },
                    { field: 'LastName', title: formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' }) },
                    { field: 'Email', title: formatMessage({ id: 'email.500d9e86', defaultMessage: 'Email' }) },
                    { field: 'UserName', title: formatMessage({ id: 'username.dceab582', defaultMessage: 'Username' }) },
                    { field: 'PartnerActive', title: formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' }) },
                    { field: 'PartnerInviteText', title: formatMessage({ id: 'status.ad856ad7', defaultMessage: 'Status' }) }
                ]}
                users={users.filter(users => (
                    users.Email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        users.FirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        users.LastName?.toLowerCase().includes(searchTerm.toLowerCase())
                ))}
                advancedSearchFields={userDefaultLookupSearchFields}
                getAllUsers={getAllUsers}
                loadingGridData={loadingGridData}
                onDetailsItemClick={onDetailsItemClick}
                partnerId={partnerDetails.ID}
                searchTerm={searchTerm}
                setLoadingGridData={val => setState({ loadingGridData: val })}
                setSearchTerm={val => setState({ searchTerm: val })}
                setUsers={val => setState({ users: val })}
                viewPermission={permissions.UserView}
            />
            <UserCreateDialog
                customCreateClick={customCreateClick}
                page="Partner"
                permissions={permissions}
                ref={userCreateDialogRef}
                setUsers={val => setState({ users: val })}
                specificPartner={partnerDetails.PartnerType}
            />
            <UserEditDialog
                onSaved={() => getAllUsers()}
                partnerId={partnerDetails.ID}
                permissions={permissions}
                ref={userEditDialogRef}
            />
        </>
    );

};
